import { getActiveAssignment, getActiveContextType } from '@/auth/activeAssignment';
import { readSessionData, writeSessionData } from '@/auth/session';
import { roleSessionKeys } from '@/query/keys';
import { getHeaderBrandId, getHeaderPropertyId } from '@/selection/headerSelection';

/**
 * The home / landing page of the ACTIVE role assignment (part 7, H4): decided
 * by the assignment's context type and its context id, never `users.type`.
 *
 * - BRAND: the context's brand profile (else the header brand);
 * - RIGHT_HOLDER: the role's start partner when the server's partner list has
 *   decided it (open question N2: the context's partner when it is inside the
 *   role's access, else the first partner inside it), else the context's
 *   partner profile (else the header partner);
 * - INTERNAL, AGENCY, no active assignment: the dashboard, or the comparison
 *   tool when the role's home module is `tools_compare`.
 *
 * The profile pages ask the server, which answers 404 when the id is outside
 * the scope; this only chooses where to go.
 *
 * No ApiProvider import (ApiProvider imports this module).
 */
export interface HomeModule {
  name_type?: string | null;
}

const idOrNull = (value: unknown): string | null => {
  if (value === null || value === undefined) return null;
  const text = String(value)
    .replace(/^"+|"+$/g, '')
    .trim();
  return /^\d+$/.test(text) && Number(text) > 0 ? text : null;
};

/**
 * The start partner of the ACTIVE RIGHT_HOLDER role (open question N2, owner
 * 2026-10-13): a user limited to a child partner does not start on the
 * organisation's main partner, which is outside the access ("Not found").
 * Decided from the server's partner list of the active scope
 * (ApiProvider.loadHomePartner); in memory only, kept with the role's session
 * data. undefined = not decided (yet); null = decided, nothing to prefer.
 */
export const getHomePartnerId = (): number | null | undefined =>
  readSessionData<{ id: number | null }>(roleSessionKeys.homePartner())?.id;

export const setHomePartnerId = (
  id: number | null,
  assignmentId: number | null | undefined = undefined,
): void =>
  writeSessionData(
    assignmentId === undefined
      ? roleSessionKeys.homePartner()
      : roleSessionKeys.homePartner(assignmentId),
    { id },
  );

export function homePath(homeModule?: HomeModule | null): string {
  const type = getActiveContextType();
  const assignment = getActiveAssignment();
  if (type === 'BRAND') {
    const brandId = idOrNull(assignment?.brandId) ?? idOrNull(getHeaderBrandId());
    if (brandId) return `/brandprofile/${brandId}`;
  }
  if (type === 'RIGHT_HOLDER') {
    const propertyId =
      idOrNull(getHomePartnerId()) ??
      idOrNull(assignment?.propertyId) ??
      idOrNull(getHeaderPropertyId());
    if (propertyId) return `/partnerProfile/${propertyId}`;
  }
  if (homeModule && typeof homeModule === 'object' && homeModule.name_type === 'tools_compare') {
    return '/comparison';
  }
  return '/dashboard';
}
